import { useEffect, useState } from 'react';
import { acceptAssignment, startAssignment, submitProof, type Assignment } from '../api';

export default function Assignments() {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    async function loadData() {
      try {
        const response = await fetch('/api/field/assignments/me');
        if (response.ok) {
          const data = await response.json();
          setAssignments(data.assignments || []);
        }
      } catch (error) {
        console.error('Failed to load assignments:', error);
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, []);
  
  const handleAccept = async (assignmentId: string) => {
    const result = await acceptAssignment(assignmentId);
    if (result.ok) {
      setAssignments(prev => 
        prev.map(a => a.id === assignmentId ? { ...a, status: 'accepted' } : a)
      );
    }
  };
  
  const handleStart = async (assignmentId: string) => {
    const result = await startAssignment(assignmentId);
    if (result.ok) {
      setAssignments(prev => 
        prev.map(a => a.id === assignmentId ? { ...a, status: 'in_progress' } : a)
      );
    }
  };
  
  const handleProofSubmit = async (assignmentId: string) => {
    const description = prompt('Describe your proof of completion:');
    if (description) {
      const result = await submitProof(assignmentId, { description });
      if (result.ok) {
        setAssignments(prev => 
          prev.map(a => a.id === assignmentId ? { ...a, status: 'proof_submitted' } : a)
        );
      }
    }
  };
  
  const getActionLabel = (assignment: Assignment) => {
    switch (assignment.status) {
      case 'assigned':
        return 'Accept';
      case 'accepted':
        return 'Start';
      case 'in_progress':
        return 'Submit Proof';
      default:
        return assignment.status;
    }
  };
  
  if (loading) {
    return (
      <div className="px-4 pt-6 pb-24">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ubuntu-green"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="px-4 pt-6 pb-24">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Work</h1>
        <p className="text-gray-600 mt-1">Your assignments and tasks</p>
      </div>
      
      {assignments.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📭</div>
          <h2 className="text-lg font-medium text-gray-900 mb-2">No assignments yet</h2>
          <p className="text-gray-600">New work will appear here when available</p>
        </div>
      ) : (
        <div className="space-y-4">
          {assignments.map((assignment) => (
            <div key={assignment.id} className="card">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-medium text-gray-900">{assignment.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="badge badge-info">{assignment.town}</span>
                    <span className="badge badge-warning">{assignment.priority}</span>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`badge ${
                    assignment.status === 'completed' 
                      ? 'badge-success' 
                      : assignment.status === 'in_progress'
                      ? 'badge-warning'
                      : 'badge-info'
                  }`}>
                    {assignment.status}
                  </span>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-3">{assignment.description}</p>
              
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {assignment.deadline ? `Due: ${new Date(assignment.deadline).toLocaleDateString()}` : 'No deadline'}
                </span>
                
                <button
                  onClick={() => {
                    switch (assignment.status) {
                      case 'assigned':
                        handleAccept(assignment.id);
                        break;
                      case 'accepted':
                        handleStart(assignment.id);
                        break;
                      case 'in_progress':
                        handleProofSubmit(assignment.id);
                        break;
                    }
                  }}
                  disabled={['completed', 'cancelled'].includes(assignment.status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    ['completed', 'cancelled'].includes(assignment.status)
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-ubuntu-green text-white active:scale-95 transition-transform'
                  }`}
                >
                  {getActionLabel(assignment)}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
